export const purchaseStatuses = ["To order", "Ordered", "Partially received", "Received"] as const;
export type PurchaseStatus = typeof purchaseStatuses[number];
export interface PurchaseTracking {
  status: PurchaseStatus;
  receivedQuantity: string;
  expectedDeliveryDate: string;
  supplierReference: string;
  notes: string;
  version: number;
  updatedAt: string;
  updatedBy: string;
}
export interface PurchaseLine {
  id: string; description: string; quantity: string; unit: string;
  supplier: string; partNumber: string; quoteReference: string;
  tracking: PurchaseTracking;
}
export interface PurchaseTracker {
  sourceId: string; lines: PurchaseLine[]; available: boolean;
}
export function emptyPurchaseTracking(): PurchaseTracking {
  return {status:"To order",receivedQuantity:"0",expectedDeliveryDate:"",supplierReference:"",notes:"",version:0,updatedAt:"",updatedBy:""};
}
export function purchaseQuantity(value: unknown): number {
  if (typeof value !== "string" || !/^\d+(?:\.\d{1,6})?$/.test(value.trim())) throw new Error("Enter a valid purchasing quantity (up to six decimal places).");
  const number=Number(value);if (!Number.isFinite(number)||number<0||number>1_000_000_000) throw new Error("Purchasing quantity is outside the supported range.");
  return number;
}
/** Operational status only. Does not change accepted quantities, costs or approval. */
export function changePurchaseTracking(previous: PurchaseTracking, quantity: string, input: unknown, actor: string, at: string): PurchaseTracking {
  if (!input || typeof input !== "object" || Array.isArray(input)) throw new Error("Purchasing status is invalid.");
  const data=input as Record<string,unknown>;
  if (Object.keys(data).some(key=>!["status","receivedQuantity","expectedDeliveryDate","supplierReference","notes","version"].includes(key))) throw new Error("Unexpected purchasing field.");
  if (!Number.isSafeInteger(data.version)||data.version!==previous.version) throw new Error("Purchasing status changed. Reload the tracker before saving.");
  if (!purchaseStatuses.includes(data.status as PurchaseStatus)) throw new Error("Choose a valid purchasing status.");
  const text=(key:string,max:number)=>{const v=data[key];if(typeof v!=="string"||v.length>max)throw new Error(`Invalid ${key}.`);return v.trim();};
  const status=data.status as PurchaseStatus,expectedDeliveryDate=text("expectedDeliveryDate",10);
  if(expectedDeliveryDate && (!/^\d{4}-\d{2}-\d{2}$/.test(expectedDeliveryDate)||!Number.isFinite(Date.parse(expectedDeliveryDate+"T00:00:00Z"))||new Date(expectedDeliveryDate+"T00:00:00Z").toISOString().slice(0,10)!==expectedDeliveryDate))throw new Error("Enter a valid expected delivery date.");
  const required=purchaseQuantity(quantity);if(required<=0)throw new Error("Confirm a positive accepted quantity before tracking receipts.");
  const entered=purchaseQuantity(text("receivedQuantity",40));
  if(status==="Partially received" && (entered<=0||entered>=required))throw new Error("A partial receipt must be greater than zero and less than the accepted quantity.");
  if((status==="To order"||status==="Ordered")&&entered!==0)throw new Error("Choose Partially received or Received when recording a receipt.");
  if(status==="Received"&&entered!==required)throw new Error("Received quantity must equal the accepted quantity. Use Partially received for a short delivery.");
  return {status,receivedQuantity:String(entered),expectedDeliveryDate,supplierReference:text("supplierReference",200),notes:text("notes",2000),version:previous.version+1,updatedAt:at,updatedBy:actor};
}
export function purchasingReadiness(lines: PurchaseLine[], today: string) {
  const outstanding=lines.filter(line=>line.tracking.status!=="Received");
  return {total:lines.length,received:lines.length-outstanding.length,outstanding:outstanding.length,
    overdue:outstanding.filter(line=>line.tracking.expectedDeliveryDate && line.tracking.expectedDeliveryDate<today).length};
}
